'use client'

import { useEffect } from 'react'

import { useConfig, useDatadog, useLogger, useWapi } from '@/app/hooks'
import { useSession } from '@/app/hooks/use-session'
import { usePathname } from '@/i18n/navigation'
import { trackLoginEvent } from '@/services/auth/client/auth-events'
import { runAutoLoginChain } from '@/services/auth/client/auto-login'
import { performFederatedLogin } from '@/services/auth/client/federated-login'
import { promptGoogleOneTap } from '@/services/auth/client/google-one-tap'
import {
    readMirrorEntry,
    readMirrorToken,
    writeMirror,
} from '@/services/auth/client/session-mirror'
import { attemptSessionRestore } from '@/services/auth/client/session-restore'
import { setSnowplowUserId } from '@/services/snowplow/set-user-id'

/**
 * Silent federated auto-login chain. Mounted globally beside the session provider. Runs
 * mirror restore/refresh → Google One-Tap; the One-Tap prompt is gated to the homepage
 * and the whole chain is deferred behind an idle callback so it never blocks landing-page
 * paint (CWV). Renders nothing.
 *
 * Browser-only behavior (the SDK prompts + token exchange) is verified via e2e / manual
 * test — the chain decision logic lives in the unit-tested `runAutoLoginChain`.
 */
export function AutoLogin() {
    const { session } = useSession()
    const pathname = usePathname()
    const config = useConfig()
    const datadog = useDatadog()
    const logger = useLogger({ module: 'auto-login' })
    const wapi = useWapi()

    useEffect(() => {
        const run = () => {
            // Each chain step is fail-safe (a failing provider must not break the page),
            // but log the failure so the silent restore isn't a fully-blind subsystem.
            // Low-cardinality metadata only (C-6) — never the token or provider payload.
            const withStepLog =
                (stepName: string, attempt: () => Promise<boolean>) =>
                async (): Promise<boolean> => {
                    try {
                        return await attempt()
                    } catch (err: unknown) {
                        logger
                            .withMetadata({
                                step: stepName,
                                error: err instanceof Error ? err.name : 'unknown',
                            })
                            .warn('auth: auto-login step failed')
                        throw err
                    }
                }

            const setRumUser = (user: { id?: string; email?: string }) => {
                // nexus DatadogUser keys the user on visitorId (→ Datadog id); for a
                // logged-in user that's the identity id.
                if (user.id) {
                    datadog.setUser({
                        visitorId: user.id,
                        cognitoId: user.id,
                        ...(user.email ? { email: user.email } : {}),
                    })
                }
            }

            const federatedLoginWith = (
                provider: 'google',
                providerToken: string,
                method: string,
                email: string | null,
            ) =>
                performFederatedLogin({
                    provider,
                    providerToken,
                    method,
                    auto: true,
                    email,
                    exchange: (p, token) => wapi.exchangeFederated(p, token),
                    writeMirror,
                    setSnowplowUserId,
                    setRumUser,
                    trackLoginEvent,
                    identify: (data) => {
                        const token = readMirrorToken()
                        return token ? wapi.identify(token, data) : Promise.resolve()
                    },
                })

            void runAutoLoginChain({
                isLoggedIn: session.isAuthenticated,
                isHomepage: pathname === '/',
                restoreSession: withStepLog('restore', () =>
                    attemptSessionRestore({
                        mirror: readMirrorEntry(),
                        refresh: (opts) => wapi.refresh(opts),
                        writeMirror,
                        setSnowplowUserId,
                        setRumUser,
                    }),
                ),
                oneTapRestore: withStepLog('one-tap', async () => {
                    const credential = await promptGoogleOneTap({
                        clientId: config.auth.googleClientId,
                        scriptUrl: config.auth.googleOneTapUrl,
                    })
                    if (!credential) {
                        return false
                    }
                    await federatedLoginWith('google', credential, 'one-tap', null)
                    return true
                }),
            })
        }

        const w = window as Window & {
            requestIdleCallback?: (cb: () => void) => number
            cancelIdleCallback?: (id: number) => void
        }
        if (typeof w.requestIdleCallback === 'function') {
            const id = w.requestIdleCallback(run)
            return () => w.cancelIdleCallback?.(id)
        }
        const timer = setTimeout(run, 1)
        return () => {
            clearTimeout(timer)
        }
    }, [session.isAuthenticated, pathname, config, datadog, logger, wapi])

    return null
}
